/**
 * 🤖 DASHBOARD INSIGHTS DE IA - NÍVEL 1
 *
 * Alertas automáticos baseados em:
 * - Portaria GM/MS 8.041/2025 (regulamento do SNT)
 * - AASLD Practice Guidance on Portal Hypertension (2024)
 *
 * Regras implementadas:
 *
 * 1. MELD VENCIDO (Art. 133, §4º da Portaria 8041):
 *    - MELD 11-18: validade 3 meses
 *    - MELD 19-25: validade 1 mês
 *    - MELD >25: validade 7 dias
 *
 * 2. IMAGEM VENCIDA - CHC (Art. 139, II, "e" da Portaria 8041):
 *    - Pacientes com situação especial (CHC) precisam TC/RM
 *      de abdome + TC tórax a cada 6 meses
 *
 * 3. CSPH PRESUMIDA (AASLD 2024 - Baveno VII):
 *    - LSM >25 kPa = CSPH (independente de plaquetas)
 *    - LSM 20-25 kPa + plaquetas <150K = CSPH
 *    - LSM 15-20 kPa + plaquetas <110K = CSPH
 *    - Presença de varizes ou ascite = CSPH presumida
 *    → Conduta: Carvedilol 6.25-12.5mg/dia
 *
 * 4. EDA VENCIDA (AASLD 2024 / Baveno VII):
 *    - Pacientes com cACLD pré-Tx sem EDA nos últimos 2 anos
 *    - Rastreamento de varizes esofágicas obrigatório
 *
 * 5. PENDÊNCIAS (das consultas/evoluções):
 *    - Pendências registradas nas consultas, agrupadas por paciente
 *
 * ORDEM DE RENDERIZAÇÃO (AI_16):
 * Alertas críticos → Alertas → Lista de Espera →
 * Situação Especial → Transplantados → Pendências → Distribuição MELD
 */

import React, { useMemo, useState, useEffect } from "react";
import {
  Bot,
  Clock,
  Camera,
  Microscope,
  Calendar,
  Star,
  BarChart2,
  ClipboardList,
  ChevronDown,
  CheckCircle,
  AlertTriangle,
  FileText,
} from "lucide-react";
import { usePacientes } from "../../pacientes/context/PacientesContext";
import { useExames } from "../../exames/context/ExamesContext";
import { useExamesImagem } from "../../exames-imagem/context/ExamesImagemContext";
import { useEvolucao } from "../../evolucao/context/EvolucaoContext";
import { usePendencias } from "../../pendencias/context/PendenciasContext";
import { theme } from "../../../shared/styles/theme";
import { collection, getDocs, orderBy, query, limit } from "firebase/firestore";
import { labelCategoriaErro } from "../../ia-analise/constants/categoriasErro";
import { db } from "../../../lib/firebase";
import { ehRespostaPositiva } from "../../../shared/utils/respostaNegativa";

// ═══════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════

interface AlertaInsight {
  id: string;
  tipo: "critico" | "alerta" | "atencao" | "info";
  icone: React.ElementType;
  titulo: string;
  descricao: string;
  quantidade: number;
  total: number; // total de referência (ex: total inscritos)
  percentual: number;
  fundamentacao: string; // base legal ou científica
  pacientes?: { id: string; nome: string; detalhe: string }[];
}

// ═══════════════════════════════════════════════════════
// FUNÇÕES UTILITÁRIAS
// ═══════════════════════════════════════════════════════

/**
 * Calcula a validade do MELD conforme Portaria 8041, Art. 133, §4º
 */
function getMELDValidade(meldScore: number): { dias: number; label: string } {
  if (meldScore > 25) return { dias: 7, label: "7 dias" };
  if (meldScore >= 19) return { dias: 30, label: "1 mês" };
  if (meldScore >= 11) return { dias: 90, label: "3 meses" };
  return { dias: 365, label: "12 meses" }; // MELD < 11 - sem obrigatoriedade específica
}

/**
 * Verifica se uma data está vencida considerando um prazo em dias
 */
function normalizarDataISO(dataStr: string): string {
  if (!dataStr) return dataStr;
  // DD/MM/AAAA → AAAA-MM-DD
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataStr)) {
    const p = dataStr.split("/");
    return `${p[2]}-${p[1]}-${p[0]}`;
  }
  return dataStr;
}
function formatarTempo(dias: number): string {
  if (dias < 0) return "—";
  const anos = Math.floor(dias / 365);
  const mesesRestantes = Math.floor((dias % 365) / 30);
  const diasRestantes = Math.floor((dias % 365) % 30);

  if (anos > 0) {
    return `${anos}a ${mesesRestantes}m`;
  } else if (mesesRestantes > 0) {
    return `${mesesRestantes}m ${diasRestantes}d`;
  } else {
    return `${diasRestantes}d`;
  }
}

function estaVencido(dataStr: string, prazoDias: number): boolean {
  if (!dataStr) return true;
  const iso = normalizarDataISO(dataStr);
  const dataCorrigida = iso.includes("T") ? iso : `${iso}T12:00:00`;
  const data = new Date(dataCorrigida);
  if (isNaN(data.getTime())) return true;
  const hoje = new Date();
  const diffMs = hoje.getTime() - data.getTime();
  const diffDias = diffMs / (1000 * 60 * 60 * 24);
  return diffDias > prazoDias;
}

function diasParaVencer(dataStr: string, prazoDias: number): number {
  if (!dataStr) return -999;
  const iso = normalizarDataISO(dataStr);
  const dataCorrigida = iso.includes("T") ? iso : `${iso}T12:00:00`;
  const data = new Date(dataCorrigida);
  if (isNaN(data.getTime())) return -999;
  const vencimento = new Date(data.getTime() + prazoDias * 24 * 60 * 60 * 1000);
  const hoje = new Date();
  return Math.ceil(
    (vencimento.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24)
  );
}

function formatarData(dataStr: string): string {
  if (!dataStr) return "N/A";
  // Se já está em DD/MM/AAAA, retornar direto
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataStr)) return dataStr;
  // Se está em AAAA-MM-DD, converter para BR
  if (/^\d{4}-\d{2}-\d{2}/.test(dataStr)) {
    const partes = dataStr.split("T")[0].split("-");
    if (partes.length === 3) return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  // Fallback: tentar new Date
  const dataCorrigida = dataStr.includes("T") ? dataStr : `${dataStr}T12:00:00`;
  const data = new Date(dataCorrigida);
  if (isNaN(data.getTime())) return dataStr;
  return data.toLocaleDateString("pt-BR");
}
// ═══════════════════════════════════════════════════════
// ACESSORES COM FALLBACK (inscricaoTx legado → campos planos)
// Garante compatibilidade entre pacientes antigos e novos
// ═══════════════════════════════════════════════════════
function getNumeroRGCT(p: any): string | undefined {
  return p.inscricaoTx?.numeroRGCT || p.numeroRGCT || p.rgct;
}
function getDataTransplante(p: any): string | undefined {
  return p.inscricaoTx?.dataTransplante || p.dataTransplante;
}
function getSituacaoEspecial(p: any): boolean {
  return (
    p.inscricaoTx?.situacaoEspecial === true || p.situacaoEspecial === true
  );
}
function getTipoSE(p: any): string {
  return p.inscricaoTx?.tipoSE || p.tipoSE || "";
}
function getDataAprovacaoSE(p: any): string {
  return p.inscricaoTx?.dataAprovacaoSE || p.dataAprovacaoSE || "";
}
function getDataInscricao(p: any): string {
  return (
    p.inscricaoTx?.dataInscricao || p.dataInscricao || p.dataInclusaoLista || ""
  );
}

// ═══════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ═══════════════════════════════════════════════════════

export default function DashboardInsightsIA() {
  const { pacientes } = usePacientes();
  const { exames } = useExames();
  const { exames: examesImagem } = useExamesImagem();
  const { evolucoes } = useEvolucao();
  const { pendencias: pendenciasGlobal } = usePendencias();
  const [expandido, setExpandido] = useState<string | null>(null);
  const [calibStats, setCalibStats] = useState<{
    total: number;
    concordo: number;
    parcial: number;
    discordo: number;
    porCategoria: { categoria: string; label: string; n: number }[];
    recentes: {
      pacienteId: string;
      voto: string;
      observacao: string | null;
      dataAnalise: string;
    }[];
  } | null>(null);

  useEffect(() => {
    const carregar = async () => {
      try {
        const q = query(
          collection(db, "calibracoes-semaforo"),
          orderBy("timestamp", "desc"),
          limit(200)
        );
        const snap = await getDocs(q);
        const docs = snap.docs.map((d) => ({ ...d.data() } as any));
        const concordo = docs.filter((d) => d.voto === "concordo").length;
        const parcial = docs.filter((d) => d.voto === "parcial").length;
        const discordo = docs.filter((d) => d.voto === "discordo").length;
        // B227 (AI_114): agrega as categorias de erro apontadas (parcial/discordo)
        // → backlog rastreável de qual regra/dimensão endurecer.
        const mapaCategorias: Record<string, number> = {};
        docs.forEach((d) => {
          if (d.categoriaErro)
            mapaCategorias[d.categoriaErro] =
              (mapaCategorias[d.categoriaErro] || 0) + 1;
        });
        const porCategoria = Object.entries(mapaCategorias)
          .map(([categoria, n]) => ({
            categoria,
            label: labelCategoriaErro(categoria),
            n,
          }))
          .sort((a, b) => b.n - a.n);
        setCalibStats({
          total: docs.length,
          concordo,
          parcial,
          discordo,
          porCategoria,
          recentes: docs.slice(0, 5).map((d) => ({
            pacienteId: d.pacienteId,
            voto: d.voto,
            observacao: d.observacao || null,
            dataAnalise: d.dataAnalise || "",
          })),
        });
      } catch (err) {
        console.error("Erro ao carregar calibrações:", err);
      }
    };
    carregar();
  }, []);

  // ═════════════════════════════════════════════════════
  // CÁLCULO DOS INSIGHTS
  // ═════════════════════════════════════════════════════

  const insights = useMemo(() => {
    const alertas: AlertaInsight[] = [];

    // ─────────────────────────────────────────────────
    // FILTRAR PACIENTES DE FÍGADO INSCRITOS EM LISTA
    // ─────────────────────────────────────────────────
    // Filtrar pacientes ativos (excluir óbito, perda de seguimento, etc.)
    const pacientesAtivos = pacientes.filter(
      (p) => !p.statusPaciente || p.statusPaciente === "ativo"
    );

    const figadoTotal = pacientesAtivos.filter(
      (p) => p.tipoAtendimento?.toLowerCase() === "figado"
    );
    const figadoInscritos = figadoTotal.filter(
      (p) => getNumeroRGCT(p) && !getDataTransplante(p)
    );

    // ═════════════════════════════════════════════════
    // 1. MELD VENCIDO (Portaria 8041, Art. 133, §4º)
    // ═════════════════════════════════════════════════
    const pacientesMeldVencido: AlertaInsight["pacientes"] = [];

    figadoInscritos.forEach((pac) => {
      // Buscar o exame laboratorial mais recente com MELD
      const examesPac = exames
        .filter((e) => e.pacienteId === pac.id && e.meld && e.meld > 0)
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

      if (examesPac.length === 0) {
        // Sem exame MELD registrado
        pacientesMeldVencido.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: "Sem MELD registrado no sistema",
        });
        return;
      }

      const ultimoExame = examesPac[0];
      const meldScore = ultimoExame.meld!;
      const validade = getMELDValidade(meldScore);
      const diasRestantes = diasParaVencer(ultimoExame.data, validade.dias);

      if (diasRestantes <= 0) {
        pacientesMeldVencido.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: `MELD ${meldScore} — vencido há ${Math.abs(
            diasRestantes
          )} dia(s) (validade: ${validade.label})`,
        });
      } else if (diasRestantes <= 7) {
        // Vence nos próximos 7 dias - alerta preventivo
        pacientesMeldVencido.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: `MELD ${meldScore} — vence em ${diasRestantes} dia(s) (val: ${
            validade.label
          }, coleta: ${formatarData(ultimoExame.data)})`,
        });
      }
    });

    if (pacientesMeldVencido.length > 0) {
      alertas.push({
        id: "meld-vencido",
        tipo: pacientesMeldVencido.some((p) => p.detalhe.includes("vencido"))
          ? "critico"
          : "alerta",
        icone: Clock,
        titulo: "MELD Vencido ou Próximo do Vencimento",
        descricao: `${pacientesMeldVencido.length} paciente(s) inscrito(s) com MELD vencido ou a vencer em até 7 dias`,
        quantidade: pacientesMeldVencido.length,
        total: figadoInscritos.length,
        percentual:
          figadoInscritos.length > 0
            ? Math.round(
                (pacientesMeldVencido.length / figadoInscritos.length) * 100
              )
            : 0,
        fundamentacao:
          "Portaria 8041/2025, Art. 133, §4º — MELD 11-18: 3 meses · MELD 19-25: 1 mês · MELD >25: 7 dias",
        pacientes: pacientesMeldVencido,
      });
    }

    // ═════════════════════════════════════════════════
    // 2. IMAGEM VENCIDA — SITUAÇÃO ESPECIAL
    //    Portaria 8041/2025, Art. 139-145
    // ═════════════════════════════════════════════════
    const pacientesImagemVencida: AlertaInsight["pacientes"] = [];

    const figadoComSE = figadoInscritos.filter((p) => getSituacaoEspecial(p));

    // Mapeamento SE → exames obrigatórios a cada 6 meses
    const exigenciasPorSE: Record<
      string,
      { descricao: string; tipos: string[][] }
    > = {
      "Carcinoma Hepatocelular (CHC)": {
        descricao: "TC/RM abdome + TC tórax",
        tipos: [
          [
            "TC Abdome",
            "TC Abdome e Tórax",
            "RM Abdome",
            "Tomografia",
            "Ressonância",
          ],
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
        ],
      },
      "Carcinoma Hepatocelular por Downstaging": {
        descricao: "TC/RM abdome trifásica + TC tórax",
        tipos: [
          [
            "TC Abdome",
            "TC Abdome e Tórax",
            "RM Abdome",
            "Tomografia",
            "Ressonância",
          ],
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
        ],
      },
      "Tumor Neuroendócrino Metastático": {
        descricao: "TC tórax + TC abdome + mapeamento ósseo",
        tipos: [
          ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ["Mapeamento Ósseo", "Cintilografia"],
        ],
      },
      Hepatoblastoma: {
        descricao: "TC tórax + TC abdome + mapeamento ósseo",
        tipos: [
          ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ["Mapeamento Ósseo", "Cintilografia"],
        ],
      },
      "Carcinoma Fibrolamelar Irressecável": {
        descricao: "TC tórax + TC abdome",
        tipos: [
          ["TC Abdome", "TC Abdome e Tórax", "Tomografia"],
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
        ],
      },
      "Hemangioendotelioma Epitelióide Primário de Fígado Irressecável": {
        descricao: "TC ou RM abdome",
        tipos: [
          [
            "TC Abdome",
            "TC Abdome e Tórax",
            "RM Abdome",
            "Tomografia",
            "Ressonância",
          ],
        ],
      },
      "Adenomatose Múltipla Irressecável": {
        descricao: "TC ou RM abdome",
        tipos: [
          [
            "TC Abdome",
            "TC Abdome e Tórax",
            "RM Abdome",
            "Tomografia",
            "Ressonância",
          ],
        ],
      },
      "Síndrome Hepatopulmonar": {
        descricao: "TC tórax + espirometria",
        tipos: [
          ["TC Tórax", "TC Abdome e Tórax", "Tomografia"],
          ["Espirometria", "Prova Função Pulmonar"],
        ],
      },
    };

    figadoComSE.forEach((pac) => {
      const tipoSE = getTipoSE(pac);
      const exigencia = exigenciasPorSE[tipoSE];

      // SE sem exigência de imagem específica (ex: Ascite Refratária, Prurido, etc.)
      if (!exigencia) return;

      const prazo = 180; // 6 meses
      const examesPac = examesImagem
        .filter((e) => e.pacienteId === pac.id)
        .sort(
          (a, b) =>
            new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
        );

      const examesVencidos: string[] = [];
      const examesAVencer: string[] = [];

      exigencia.tipos.forEach((grupoTipos) => {
        // Buscar o exame mais recente que pertence a este grupo
        const ultimoDoGrupo = examesPac.find((e) =>
          grupoTipos.includes(e.tipoExame)
        );
        if (!ultimoDoGrupo) {
          examesVencidos.push(`${grupoTipos[0]} não registrado`);
        } else if (estaVencido(ultimoDoGrupo.dataExame, prazo)) {
          const dias = Math.floor(
            (new Date().getTime() -
              new Date(ultimoDoGrupo.dataExame).getTime()) /
              (1000 * 60 * 60 * 24)
          );
          examesVencidos.push(
            `${ultimoDoGrupo.tipoExame}: ${formatarData(
              ultimoDoGrupo.dataExame
            )} (${dias} dias atrás)`
          );
        } else {
          const diasRestantes = diasParaVencer(ultimoDoGrupo.dataExame, prazo);
          if (diasRestantes <= 30) {
            examesAVencer.push(
              `${ultimoDoGrupo.tipoExame} vence em ${diasRestantes} dias`
            );
          }
        }
      });

      if (examesVencidos.length > 0) {
        pacientesImagemVencida.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: `SE: ${tipoSE} — Exige: ${
            exigencia.descricao
          } · Pendente: ${examesVencidos.join(" · ")}`,
        });
      } else if (examesAVencer.length > 0) {
        pacientesImagemVencida.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: `SE: ${tipoSE} — ${examesAVencer.join(" · ")}`,
        });
      }
    });

    if (pacientesImagemVencida.length > 0) {
      alertas.push({
        id: "imagem-vencida-chc",
        tipo: pacientesImagemVencida.some(
          (p) =>
            p.detalhe.includes("não registrado") ||
            p.detalhe.includes("dias atrás")
        )
          ? "critico"
          : "atencao",
        icone: Camera,
        titulo: "Imagem Vencida — Situação Especial",
        descricao: `${pacientesImagemVencida.length} paciente(s) com SE necessitam renovação de exames de imagem`,
        quantidade: pacientesImagemVencida.length,
        total: figadoComSE.length,
        percentual:
          figadoComSE.length > 0
            ? Math.round(
                (pacientesImagemVencida.length / figadoComSE.length) * 100
              )
            : 0,
        fundamentacao:
          "Portaria 8041/2025, Art. 139-145 — Cada SE tem exigências específicas de imagem a cada 6 meses",
        pacientes: pacientesImagemVencida,
      });
    }

    // ═════════════════════════════════════════════════
    // 3. CSPH PRESUMIDA (AASLD 2024 / Baveno VII)
    //    Critérios:
    //    a) Elastografia (Rule of Five)
    //    b) Achados da IA: ascite, shunts, varizes
    //    c) Varizes/colaterais em laudos (fallback textual)
    // ═════════════════════════════════════════════════
    const pacientesCSPH: AlertaInsight["pacientes"] = [];

    // Termos para busca textual em laudos (fallback)
    const termosVarizes = [
      "variz",
      "varizes",
      "varicosa",
      "varicosas",
      "varicosidade",
      "cordões varicosos",
      "cordoes varicosos",
    ];
    const termosColaterais = [
      "circulação colateral",
      "circulacao colateral",
      "recanalização",
      "recanalizacao",
      "recanalizada",
      "recanalizado",
      "paraumbilical",
      "veia paraumbilical",
      "esplenorrenal",
      "shunt esplenorrenal",
      "hepatofugal",
      "fluxo hepatofugal",
      "gastropatia hipertensiva",
      "gastropatia congestiva",
    ];

    const laudoContemTermo = (
      laudo: string,
      termos: string[]
    ): string | null => {
      if (!laudo) return null;
      const laudoLower = laudo.toLowerCase();
      for (const termo of termos) {
        if (laudoLower.includes(termo.toLowerCase())) return termo;
      }
      return null;
    };

    // Apenas pré-transplante de fígado (não transplantados)
    const figadoPreTx = figadoTotal.filter((p) => !getDataTransplante(p));
    const pacientesCSPHIds = new Set<string>();

    figadoPreTx.forEach((pac) => {
      const motivos: string[] = [];

      // Exames de imagem deste paciente
      const imagensPac = examesImagem.filter((e) => e.pacienteId === pac.id);

      // ── CRITÉRIO A: Achados estruturados da IA ──
      for (const img of imagensPac) {
        if (!img.achados) continue;
        const nomeExame = img.tipoExame || "Exame";
        const dataExame = img.dataExame
          ? formatarData(img.dataExame)
          : "s/data";

        if (img.achados.ascite === "SIM") {
          motivos.push(`${nomeExame} (${dataExame}): Ascite presente (IA)`);
        }

        // B361 (AI_156): fonte única da negação (antes: lista local duplicada aqui).
        const shNegativo = !ehRespostaPositiva(img.achados.shunts);
        if (img.achados.shunts && !shNegativo) {
          motivos.push(
            `${nomeExame} (${dataExame}): Shunts — ${img.achados.shunts} (IA)`
          );
        }

        const tpVal = img.achados.trombosePortal?.toLowerCase().trim() || "";
        const tpNegativo =
          !tpVal ||
          tpVal.startsWith("não") ||
          tpVal.startsWith("nao") ||
          tpVal.startsWith("ausent") ||
          tpVal.startsWith("sem ") ||
          tpVal === "nenhum" ||
          tpVal === "nenhuma";
        if (img.achados.trombosePortal && !tpNegativo) {
          motivos.push(
            `${nomeExame} (${dataExame}): Trombose portal — ${img.achados.trombosePortal} (IA)`
          );
        }

        const espVal = img.achados.esplenomegalia?.toLowerCase().trim() || "";
        const espNegativo =
          !espVal ||
          espVal.startsWith("não") ||
          espVal.startsWith("nao") ||
          espVal.startsWith("ausent") ||
          espVal.startsWith("sem ") ||
          espVal === "normal" ||
          espVal === "nenhum" ||
          espVal.includes("sem particularidades") ||
          espVal.includes("dimensões normais") ||
          espVal.includes("dimensoes normais");
        if (img.achados.esplenomegalia && !espNegativo) {
          motivos.push(
            `${nomeExame} (${dataExame}): Esplenomegalia — ${img.achados.esplenomegalia} (IA)`
          );
        }

        if (img.achados.outrosAchados) {
          const termoVariz = laudoContemTermo(
            img.achados.outrosAchados,
            termosVarizes
          );
          if (termoVariz) {
            motivos.push(
              `${nomeExame} (${dataExame}): "${termoVariz}" em outros achados (IA)`
            );
          }
          const termoCol = laudoContemTermo(
            img.achados.outrosAchados,
            termosColaterais
          );
          if (termoCol) {
            motivos.push(
              `${nomeExame} (${dataExame}): "${termoCol}" em outros achados (IA)`
            );
          }
        }
      }

      // ── CRITÉRIO B: Elastografia (Rule of Five) ──
      const elastografias = imagensPac
        .filter((e) => e.tipoExame === "Elastografia")
        .sort(
          (a, b) =>
            new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
        );

      const examesComPlaquetas = exames
        .filter(
          (e) => e.pacienteId === pac.id && e.plaquetas && e.plaquetas > 0
        )
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

      const ultimaElasto = elastografias[0];
      const ultimasPlaquetas = examesComPlaquetas[0]?.plaquetas;

      if (ultimaElasto?.laudo) {
        const matchKpa = ultimaElasto.laudo.match(/(\d+[\.,]?\d*)\s*kPa/i);
        if (matchKpa) {
          const lsm = parseFloat(matchKpa[1].replace(",", "."));
          if (lsm > 25) {
            motivos.push(
              `Elastografia: LSM ${lsm} kPa (>25) — CSPH confirmada`
            );
          } else if (
            lsm >= 20 &&
            lsm <= 25 &&
            ultimasPlaquetas &&
            ultimasPlaquetas < 150000
          ) {
            motivos.push(
              `Elastografia: LSM ${lsm} kPa + Plt ${(
                ultimasPlaquetas / 1000
              ).toFixed(0)}K (<150K)`
            );
          } else if (
            lsm >= 15 &&
            lsm < 20 &&
            ultimasPlaquetas &&
            ultimasPlaquetas < 110000
          ) {
            motivos.push(
              `Elastografia: LSM ${lsm} kPa + Plt ${(
                ultimasPlaquetas / 1000
              ).toFixed(0)}K (<110K)`
            );
          }
        }
      }

      // ── CRITÉRIO C: Busca textual em laudos (fallback) ──
      if (motivos.length === 0) {
        const endoscopias = imagensPac
          .filter((e) => e.tipoExame === "Endoscopia")
          .sort(
            (a, b) =>
              new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
          );
        if (endoscopias[0]?.laudo) {
          const termoEncontrado = laudoContemTermo(
            endoscopias[0].laudo,
            termosVarizes
          );
          if (termoEncontrado) {
            motivos.push(
              `Endoscopia (${formatarData(
                endoscopias[0].dataExame
              )}): "${termoEncontrado}" no laudo`
            );
          }
        }

        const imagensAbdome = imagensPac
          .filter(
            (e) =>
              e.tipoExame === "Ultrassonografia" ||
              e.tipoExame === "Tomografia" ||
              e.tipoExame === "Ressonância Magnética"
          )
          .sort(
            (a, b) =>
              new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
          );

        for (const img of imagensAbdome.slice(0, 3)) {
          if (img.laudo) {
            const termoEncontrado = laudoContemTermo(img.laudo, [
              ...termosVarizes,
              ...termosColaterais,
            ]);
            if (termoEncontrado) {
              motivos.push(
                `${img.tipoExame} (${formatarData(
                  img.dataExame
                )}): "${termoEncontrado}" no laudo`
              );
              break;
            }
          }
        }

        for (const img of imagensPac) {
          if (img.resumoAchados) {
            const termoEncontrado = laudoContemTermo(img.resumoAchados, [
              ...termosVarizes,
              ...termosColaterais,
              "hipertensão portal",
              "hipertensao portal",
            ]);
            if (termoEncontrado) {
              motivos.push(
                `${img.tipoExame} (${formatarData(
                  img.dataExame
                )}): "${termoEncontrado}" na análise de IA`
              );
              break;
            }
          }
        }
      }

      // Deduplicar motivos e adicionar paciente
      const motivosUnicos = motivos.filter((m, i) => motivos.indexOf(m) === i);

      // Verificar se já está em uso de NSBB (carvedilol/propranolol)
      // Fontes: campo medicacoesEmUso do cadastro + HMA e revisão de sistemas das evoluções
      const termosNSBB = ["carvedilol", "propranolol", "nadolol"];
      const medicacoesCadastro = (
        (pac as any).medicacoesEmUso || ""
      ).toLowerCase();
      const evolucoesPac = evolucoes.filter(
        (e) => e.pacienteId === pac.id && e.cancelado !== true
      );
      const textoEvolucao = evolucoesPac
        .map((e) =>
          [
            (e as any).queixaPrincipal || "",
            (e as any).historiaDoencaAtual || "",
            (e as any).revisaoSistemas || "",
            (e as any).exameFisico || "",
            (e as any).conduta || "",
            (e as any).evolucaoLivre || "", // B90: campo único
          ].join(" ")
        )
        .join(" ")
        .toLowerCase();
      const jaUsaNSBB = termosNSBB.some(
        (t) => medicacoesCadastro.includes(t) || textoEvolucao.includes(t)
      );

      if (
        motivosUnicos.length > 0 &&
        !pacientesCSPHIds.has(pac.id) &&
        !jaUsaNSBB
      ) {
        pacientesCSPHIds.add(pac.id);
        const resumo = motivosUnicos.slice(0, 2).join(" · ");
        const extras =
          motivosUnicos.length > 2 ? ` (+${motivosUnicos.length - 2})` : "";
        pacientesCSPH.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: `${resumo}${extras} → Carvedilol 6.25-12.5mg/dia`,
        });
      }
    });

    if (pacientesCSPH.length > 0) {
      alertas.push({
        id: "csph-detectada",
        tipo: "atencao", // ← AI_16: mudado de "info" para "atencao" — CSPH exige conduta (carvedilol)
        icone: Microscope,
        titulo: "CSPH Presumida — Avaliar Carvedilol",
        descricao: `${pacientesCSPH.length} paciente(s) com critérios clínicos para CSPH`,
        quantidade: pacientesCSPH.length,
        total: figadoPreTx.length,
        percentual:
          figadoPreTx.length > 0
            ? Math.round((pacientesCSPH.length / figadoPreTx.length) * 100)
            : 0,
        fundamentacao:
          "AASLD 2024 — Achados de IA (ascite, shunts, varizes, colaterais), elastografia (Rule of Five), ou laudos → Carvedilol para prevenir descompensação",
        pacientes: pacientesCSPH,
      });
    }

    // ═════════════════════════════════════════════════
    // 3b. EDA VENCIDA (AASLD 2024 / Baveno VII)
    //     Todos pré-Tx de fígado:
    //     - Padrão: EDA a cada 2 anos (doença ativa / sem EDA prévia)
    //     - Se última EDA negativa (sem varizes): 3 anos (Baveno VII)
    // ═════════════════════════════════════════════════
    const pacientesEdaVencida: AlertaInsight["pacientes"] = [];
    const prazoEdaPadrao = 730; // 2 anos — doença ativa ou sem EDA prévia
    const prazoEdaNegativa = 1095; // 3 anos — última EDA sem varizes (Baveno VII)

    // Termos que indicam varizes presentes no laudo da EDA
    const termosVarizesEda = [
      "variz",
      "varizes",
      "varicosa",
      "varicosas",
      "varicosidade",
      "cordões varicosos",
      "cordoes varicosos",
      "ectasia vascular",
    ];

    figadoPreTx.forEach((pac) => {
      // Só disparar alerta de EDA se paciente já tem pelo menos uma consulta registrada
      const temConsulta = evolucoes.some(
        (e) => e.pacienteId === pac.id && e.cancelado !== true
      );
      if (!temConsulta) return;

      const endoscopiasPac = examesImagem
        .filter((e) => e.pacienteId === pac.id && e.tipoExame === "Endoscopia")
        .sort(
          (a, b) =>
            new Date(b.dataExame).getTime() - new Date(a.dataExame).getTime()
        );

      if (endoscopiasPac.length === 0) {
        pacientesEdaVencida.push({
          id: pac.id,
          nome: pac.nome,
          detalhe: "Nenhuma EDA registrada — rastreamento de varizes pendente",
        });
      } else {
        const ultimaEda = endoscopiasPac[0];

        // Verificar se a última EDA encontrou varizes (laudo + achados IA)
        const laudoTexto = (ultimaEda.laudo || "").toLowerCase();
        const resumoTexto = (ultimaEda.resumoAchados || "").toLowerCase();
        const textoCompleto = `${laudoTexto} ${resumoTexto}`;
        const temVarizes = termosVarizesEda.some((t) =>
          textoCompleto.includes(t)
        );

        // EDA negativa → 3 anos; EDA com varizes ou sem laudo → 2 anos
        const prazo =
          !temVarizes && laudoTexto.length > 0
            ? prazoEdaNegativa
            : prazoEdaPadrao;

        if (estaVencido(ultimaEda.dataExame, prazo)) {
          const diasAtras = Math.floor(
            (new Date().getTime() - new Date(ultimaEda.dataExame).getTime()) /
              (1000 * 60 * 60 * 24)
          );
          const mesesAtras = Math.floor(diasAtras / 30);
          const tipoAlerta = temVarizes
            ? `Última EDA: ${formatarData(
                ultimaEda.dataExame
              )} (há ${mesesAtras} meses) — varizes documentadas`
            : `Última EDA: ${formatarData(
                ultimaEda.dataExame
              )} (há ${mesesAtras} meses) — sem varizes`;
          pacientesEdaVencida.push({
            id: pac.id,
            nome: pac.nome,
            detalhe: tipoAlerta,
          });
        }
      }
    });

    if (pacientesEdaVencida.length > 0) {
      alertas.push({
        id: "eda-vencida",
        tipo: "atencao",
        icone: AlertTriangle,
        titulo: "EDA Vencida — Rastreamento de Varizes",
        descricao: `${pacientesEdaVencida.length} paciente(s) pré-Tx sem EDA nos últimos 2 anos`,
        quantidade: pacientesEdaVencida.length,
        total: figadoPreTx.length,
        percentual:
          figadoPreTx.length > 0
            ? Math.round(
                (pacientesEdaVencida.length / figadoPreTx.length) * 100
              )
            : 0,
        fundamentacao:
          "Baveno VII / AASLD 2024 — Pacientes com cACLD: EDA a cada 2 anos (doença ativa) ou 3 anos (controlada). Sem NSBB: rastreamento obrigatório.",
        pacientes: pacientesEdaVencida,
      });
    }

    // ═════════════════════════════════════════════════
    // 4. SEM CONSULTA HÁ MAIS DE 6 MESES
    //    (Portaria 8041 - acompanhamento obrigatório)
    // ═════════════════════════════════════════════════
    const pacientesSemConsulta: AlertaInsight["pacientes"] = [];

    pacientesAtivos.forEach((pac) => {
      const evolucoesPac = evolucoes
        .filter((e) => e.pacienteId === pac.id && e.cancelado !== true)
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

      const ultimaConsulta = evolucoesPac[0];
      const hoje = new Date();

      if (!ultimaConsulta) {
        const dataCadastroStr = pac.dataCadastro?.includes("T")
          ? pac.dataCadastro
          : `${pac.dataCadastro}T12:00:00`;
        const dataCadastro = new Date(dataCadastroStr);
        const diffDias = Math.floor(
          (hoje.getTime() - dataCadastro.getTime()) / (1000 * 60 * 60 * 24)
        );
        if (diffDias > 30) {
          pacientesSemConsulta.push({
            id: pac.id,
            nome: pac.nome,
            detalhe: `Nenhuma consulta registrada (cadastro há ${diffDias} dias)`,
          });
        }
      } else {
        const dataUltima = new Date(
          ultimaConsulta.data?.includes("T")
            ? ultimaConsulta.data
            : `${ultimaConsulta.data}T12:00:00`
        );
        const diffDias = Math.floor(
          (hoje.getTime() - dataUltima.getTime()) / (1000 * 60 * 60 * 24)
        );

        if (diffDias > 365) {
          const meses = Math.floor(diffDias / 30);
          pacientesSemConsulta.push({
            id: pac.id,
            nome: pac.nome,
            detalhe: `Última consulta: ${formatarData(
              ultimaConsulta.data
            )} (há ${meses} meses) — Avaliar perda de seguimento`,
          });
        } else if (diffDias > 180) {
          const meses = Math.floor(diffDias / 30);
          pacientesSemConsulta.push({
            id: pac.id,
            nome: pac.nome,
            detalhe: `Última consulta: ${formatarData(
              ultimaConsulta.data
            )} (há ${meses} meses)`,
          });
        }
      }
    });

    if (pacientesSemConsulta.length > 0) {
      const criticos = pacientesSemConsulta.filter(
        (p) =>
          p.detalhe.includes("perda de seguimento") ||
          p.detalhe.includes("Nenhuma consulta")
      );
      alertas.push({
        id: "sem-consulta",
        tipo: criticos.length > 0 ? "alerta" : "atencao",
        icone: Calendar,
        titulo: "Pacientes Sem Consulta Recente",
        descricao: `${pacientesSemConsulta.length} paciente(s) sem consulta há mais de 6 meses`,
        quantidade: pacientesSemConsulta.length,
        total: pacientesAtivos.length,
        percentual:
          pacientesAtivos.length > 0
            ? Math.round(
                (pacientesSemConsulta.length / pacientesAtivos.length) * 100
              )
            : 0,
        fundamentacao:
          "Acompanhamento regular obrigatório — >1 ano: avaliar perda de seguimento ou alterar status do paciente",
        pacientes: pacientesSemConsulta,
      });
    }

    // ═════════════════════════════════════════════════
    // 5. PACIENTES COM SITUAÇÃO ESPECIAL
    // ═════════════════════════════════════════════════
    const pacientesComSE: AlertaInsight["pacientes"] = [];

    figadoInscritos.forEach((pac) => {
      if (!getSituacaoEspecial(pac)) return;

      const tipoSE = getTipoSE(pac) || "Não especificada";
      const dataAprovacao = getDataAprovacaoSE(pac)
        ? formatarData(getDataAprovacaoSE(pac))
        : "N/A";

      const ultimoExame = exames
        .filter((e) => e.pacienteId === pac.id && e.meld && e.meld > 0)
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        )[0];

      const meldBiologico = ultimoExame?.meld || 0;

      let meldAjustado = "";
      if (
        tipoSE.toLowerCase().includes("chc") ||
        tipoSE.toLowerCase().includes("hepatocarcinoma")
      ) {
        const dataAprovSE = getDataAprovacaoSE(pac);
        if (dataAprovSE) {
          let dataISO = dataAprovSE;
          if (/^\d{2}\/\d{2}\/\d{4}$/.test(dataAprovSE)) {
            const p = dataAprovSE.split("/");
            dataISO = `${p[2]}-${p[1]}-${p[0]}`;
          }
          const diasDesdeAprov = Math.floor(
            (new Date().getTime() - new Date(dataISO + "T12:00:00").getTime()) /
              (1000 * 60 * 60 * 24)
          );
          if (diasDesdeAprov > 180) {
            meldAjustado = "MELD 29";
          } else if (diasDesdeAprov > 90) {
            meldAjustado = "MELD 24";
          } else {
            meldAjustado = "MELD 20";
          }
        } else {
          meldAjustado = "MELD 20 (inicial)";
        }
      }

      const detalhePartes = [`SE: ${tipoSE}`, `Aprovação: ${dataAprovacao}`];
      if (meldBiologico > 0)
        detalhePartes.push(`MELD biológico: ${meldBiologico}`);
      if (meldAjustado) detalhePartes.push(`MELD ajustado: ${meldAjustado}`);

      pacientesComSE.push({
        id: pac.id,
        nome: pac.nome,
        detalhe: detalhePartes.join(" · "),
      });
    });

    if (pacientesComSE.length > 0) {
      alertas.push({
        id: "situacao-especial",
        tipo: "info",
        icone: Star,
        titulo: "Pacientes com Situação Especial",
        descricao: `${pacientesComSE.length} paciente(s) inscrito(s) com SE aprovada`,
        quantidade: pacientesComSE.length,
        total: figadoInscritos.length,
        percentual:
          figadoInscritos.length > 0
            ? Math.round((pacientesComSE.length / figadoInscritos.length) * 100)
            : 0,
        fundamentacao:
          "Portaria 8041/2025, Art. 139-145 — CHC: MELD 20 inicial → 24 (3m) → 29 (6m). Requer imagem a cada 6 meses.",
        pacientes: pacientesComSE,
      });
    }

    // ═════════════════════════════════════════════════
    // 6. DISTRIBUIÇÃO POR FAIXA MELD (Visão geral)
    // ═════════════════════════════════════════════════
    let meld6a10 = 0;
    let meld11a18 = 0;
    let meld19a25 = 0;
    let meldMaior25 = 0;
    let semMeld = 0;

    figadoInscritos.forEach((pac) => {
      const examesPac = exames
        .filter((e) => e.pacienteId === pac.id && e.meld && e.meld > 0)
        .sort(
          (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
        );

      if (examesPac.length === 0) {
        semMeld++;
        return;
      }

      const score = examesPac[0].meld!;
      if (score <= 10) meld6a10++;
      else if (score <= 18) meld11a18++;
      else if (score <= 25) meld19a25++;
      else meldMaior25++;
    });

    // ═════════════════════════════════════════════════
    // LISTA DE ESPERA — ordenada por MELD decrescente
    // ═════════════════════════════════════════════════
    const listaEspera = figadoInscritos
      .map((pac) => {
        const ultimoExame = exames
          .filter((e) => e.pacienteId === pac.id && e.meld && e.meld > 0)
          .sort(
            (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
          )[0];
        const meld = ultimoExame?.meld || 0;
        const dataInscricao = getDataInscricao(pac);
        const diasEmLista = dataInscricao
          ? Math.floor(
              (new Date().getTime() -
                new Date(
                  normalizarDataISO(dataInscricao).includes("T")
                    ? normalizarDataISO(dataInscricao)
                    : `${normalizarDataISO(dataInscricao)}T12:00:00`
                ).getTime()) /
                (1000 * 60 * 60 * 24)
            )
          : null;
        return {
          id: pac.id,
          nome: pac.nome,
          meld,
          meldData: ultimoExame?.data || "",
          diasEmLista,
          situacaoEspecial: getSituacaoEspecial(pac),
          tipoSE: getTipoSE(pac),
        };
      })
      .sort((a, b) => b.meld - a.meld);

    // ═════════════════════════════════════════════════
    // TRANSPLANTADOS — ordenados por data do Tx
    // ═════════════════════════════════════════════════
    const transplantados = pacientesAtivos
      .filter((p) => getDataTransplante(p))
      .map((pac) => {
        const dataTx = getDataTransplante(pac)!;
        const dataTxISO = normalizarDataISO(dataTx);
        const diasPosTx = Math.floor(
          (new Date().getTime() -
            new Date(
              dataTxISO.includes("T") ? dataTxISO : `${dataTxISO}T12:00:00`
            ).getTime()) /
            (1000 * 60 * 60 * 24)
        );
        const examesPac = exames
          .filter((e) => e.pacienteId === pac.id)
          .sort(
            (a, b) => new Date(b.data).getTime() - new Date(a.data).getTime()
          );
        const ultimoExame = examesPac[0];
        const complicacoes: string[] = [];
        if (ultimoExame) {
          const limiteAST = diasPosTx <= 7 ? 2000 : diasPosTx <= 30 ? 150 : 200;
          const limiteALT = diasPosTx <= 7 ? 2000 : diasPosTx <= 30 ? 150 : 200;
          const limiteBT = diasPosTx <= 7 ? 10 : diasPosTx <= 30 ? 5 : 3;

          if (ultimoExame.tgo && ultimoExame.tgo > limiteAST)
            complicacoes.push(`AST/TGO ${ultimoExame.tgo} (>${limiteAST})`);
          if (ultimoExame.tgp && ultimoExame.tgp > limiteALT)
            complicacoes.push(`ALT/TGP ${ultimoExame.tgp} (>${limiteALT})`);
          if (
            ultimoExame.bilirrubinaTotal &&
            ultimoExame.bilirrubinaTotal > limiteBT
          )
            complicacoes.push(
              `Bilirrubina ${ultimoExame.bilirrubinaTotal} (>${limiteBT})`
            );
          if (ultimoExame.creatinina && ultimoExame.creatinina > 2)
            complicacoes.push("Creatinina elevada");
        }
        return {
          id: pac.id,
          nome: pac.nome,
          dataTx,
          diasPosTx,
          tipoAtendimento: pac.tipoAtendimento || "",
          complicacoes,
        };
      })
      .sort(
        (a, b) =>
          new Date(normalizarDataISO(b.dataTx)).getTime() -
          new Date(normalizarDataISO(a.dataTx)).getTime()
      );

    // ═════════════════════════════════════════════════
    // PENDÊNCIAS (módulo próprio — PendenciasContext)
    // Apenas pendências não concluídas, agrupadas por paciente
    // ═════════════════════════════════════════════════
    const pendenciasPorPaciente: {
      id: string;
      nome: string;
      pendencias: string[];
      dataConsulta: string;
    }[] = [];

    // Agrupar pendências por pacienteId
    const mapaPendencias = new Map<
      string,
      { nome: string; itens: { descricao: string; criadoEm: string }[] }
    >();

    pendenciasGlobal.forEach((pend) => {
      if (pend.concluido) return; // segurança — o context já filtra, mas previne
      if (!mapaPendencias.has(pend.pacienteId)) {
        mapaPendencias.set(pend.pacienteId, {
          nome: pend.pacienteNome,
          itens: [],
        });
      }
      mapaPendencias.get(pend.pacienteId)!.itens.push({
        descricao: pend.descricao,
        criadoEm: pend.criadoEm,
      });
    });

    mapaPendencias.forEach((valor, pacienteId) => {
      // Ordenar itens por data (mais recente primeiro)
      valor.itens.sort(
        (a, b) =>
          new Date(b.criadoEm).getTime() - new Date(a.criadoEm).getTime()
      );
      pendenciasPorPaciente.push({
        id: pacienteId,
        nome: valor.nome,
        pendencias: valor.itens.map((i) => i.descricao),
        dataConsulta: valor.itens[0].criadoEm, // data da pendência mais recente
      });
    });

    // ─────────────────────────────────────────────────
    // Retornar todos os insights calculados
    // ─────────────────────────────────────────────────
    return {
      alertas,
      distribuicaoMeld: {
        meld6a10,
        meld11a18,
        meld19a25,
        meldMaior25,
        semMeld,
        total: figadoInscritos.length,
      },
      resumo: {
        totalFigado: figadoTotal.length,
        inscritos: figadoInscritos.length,
        comSE: figadoComSE.length,
      },
      listaEspera,
      transplantados,
      pendenciasPorPaciente, // ← AI_16: novo
    };
  }, [pacientes, exames, examesImagem, evolucoes, pendenciasGlobal]);

  // ═════════════════════════════════════════════════════
  // CORES POR TIPO DE ALERTA
  // ═════════════════════════════════════════════════════
  const corAlerta = {
    critico: {
      bg: "#fef2f2",
      border: "#ef4444",
      text: "#991b1b",
      badge: "#ef4444",
    },
    alerta: {
      bg: "#fffbeb",
      border: "#f59e0b",
      text: "#92400e",
      badge: "#f59e0b",
    },
    atencao: {
      bg: "#fff7ed",
      border: "#f97316",
      text: "#9a3412",
      badge: "#f97316",
    },
    info: {
      bg: "#eff6ff",
      border: "#3b82f6",
      text: "#1e3a8a",
      badge: "#3b82f6",
    },
  };

  // ═════════════════════════════════════════════════════
  // AI_16: Separar alertas acionáveis dos informativos
  // Alertas acionáveis (critico/alerta/atencao) = exigem conduta
  // Painel informativo (info) = situação especial = visão geral
  // ═════════════════════════════════════════════════════
  const alertasAcionaveis = insights.alertas.filter(
    (a) => a.tipo === "critico" || a.tipo === "alerta" || a.tipo === "atencao"
  );
  const alertaSituacaoEspecial = insights.alertas.find(
    (a) => a.id === "situacao-especial"
  );

  // ═════════════════════════════════════════════════════
  // AI_16: Badge conta apenas alertas acionáveis
  // ═════════════════════════════════════════════════════
  const totalAlertasAcionaveis = alertasAcionaveis.reduce(
    (acc, a) => acc + a.quantidade,
    0
  );

  // ═════════════════════════════════════════════════════
  // SE NÃO TEM DADOS DE FÍGADO, NÃO MOSTRAR
  // ═════════════════════════════════════════════════════
  if (insights.resumo.totalFigado === 0) {
    return (
      <div
        style={{
          background: `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[800]} 100%)`,
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.shadows.md,
          padding: "24px",
          marginTop: "24px",
          color: "white",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Bot size={32} />
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "bold",
              margin: 0,
              fontFamily: theme.fonts.sans,
            }}
          >
            Insights de IA
          </h3>
        </div>
        <p
          style={{
            fontSize: "14px",
            margin: "12px 0 0",
            opacity: 0.9,
            fontFamily: theme.fonts.sans,
          }}
        >
          Cadastre pacientes de Transplante Hepático para ativar os alertas
          automáticos baseados na Portaria 8041/2025 e diretrizes AASLD.
        </p>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════
  // COMPONENTE DE ALERTA (reutilizável)
  // ═════════════════════════════════════════════════════
  const renderAlerta = (alerta: AlertaInsight) => {
    const cores = corAlerta[alerta.tipo];
    const isExpanded = expandido === alerta.id;

    return (
      <div
        key={alerta.id}
        style={{
          background: cores.bg,
          border: `1px solid ${cores.border}`,
          borderLeft: `4px solid ${cores.border}`,
          borderRadius: theme.borderRadius.lg,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "14px 16px",
            cursor: alerta.pacientes?.length ? "pointer" : "default",
            display: "flex",
            alignItems: "flex-start",
            gap: "12px",
          }}
          onClick={() => {
            if (alerta.pacientes?.length) {
              setExpandido(isExpanded ? null : alerta.id);
            }
          }}
        >
          <alerta.icone
            size={22}
            style={{ flexShrink: 0, color: cores.text }}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: cores.text,
                  fontFamily: theme.fonts.sans,
                }}
              >
                {alerta.titulo}
              </span>
              <span
                style={{
                  background: cores.badge,
                  color: "white",
                  fontSize: "11px",
                  fontWeight: "700",
                  padding: "2px 8px",
                  borderRadius: theme.borderRadius.full,
                  fontFamily: theme.fonts.sans,
                }}
              >
                {alerta.quantidade}/{alerta.total} ({alerta.percentual}%)
              </span>
            </div>
            <p
              style={{
                fontSize: "12px",
                color: cores.text,
                margin: "4px 0 0",
                opacity: 0.85,
                fontFamily: theme.fonts.sans,
              }}
            >
              {alerta.descricao}
            </p>
            <p
              style={{
                fontSize: "11px",
                color: cores.text,
                margin: "6px 0 0",
                opacity: 0.65,
                fontStyle: "italic",
                fontFamily: theme.fonts.sans,
              }}
            >
              {alerta.fundamentacao}
            </p>
          </div>
          {alerta.pacientes && alerta.pacientes.length > 0 && (
            <span
              style={{
                fontSize: "16px",
                color: cores.text,
                opacity: 0.5,
                transition: "transform 0.2s",
                transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                flexShrink: 0,
              }}
            >
              ▼
            </span>
          )}
        </div>

        {isExpanded && alerta.pacientes && (
          <div
            style={{
              borderTop: `1px solid ${cores.border}`,
              padding: "12px 16px",
              background: "rgba(255,255,255,0.5)",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "6px",
              }}
            >
              {alerta.pacientes.map((pac, idx) => (
                <div
                  key={pac.id}
                  style={{
                    padding: "8px 10px",
                    background:
                      idx % 2 === 0 ? "rgba(255,255,255,0.6)" : "transparent",
                    borderRadius: theme.borderRadius.md,
                    fontSize: "12px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "600",
                      color: cores.text,
                      fontSize: "12px",
                    }}
                  >
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(
                          `${window.location.origin}/prontuario/${pac.id}`,
                          "_blank"
                        );
                      }}
                      style={{
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                      title="Abrir prontuário em nova aba"
                    >
                      {pac.nome}
                    </span>
                  </p>
                  <p
                    style={{
                      margin: "3px 0 0",
                      color: cores.text,
                      opacity: 0.75,
                      fontSize: "11px",
                      lineHeight: "1.4",
                      wordBreak: "break-word",
                    }}
                  >
                    {pac.detalhe}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ═════════════════════════════════════════════════════
  // RENDERIZAÇÃO — AI_16: Nova ordem
  // 1. Alertas acionáveis (críticos → alerta → atenção)
  // 2. Lista de Espera
  // 3. Situação Especial
  // 4. Transplantados
  // 5. Pendências
  // 6. Distribuição MELD
  // ═════════════════════════════════════════════════════

  return (
    <div style={{ marginTop: "24px" }}>
      {/* ─── CABEÇALHO ─── */}
      <div
        style={{
          background:
            totalAlertasAcionaveis > 0
              ? `linear-gradient(135deg, #1e293b 0%, #334155 100%)`
              : `linear-gradient(135deg, ${theme.primary[600]} 0%, ${theme.primary[800]} 100%)`,
          borderRadius: `${theme.borderRadius.xl} ${theme.borderRadius.xl} 0 0`,
          padding: "20px 24px",
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Bot size={28} />
            <div>
              <h3
                style={{
                  fontSize: "18px",
                  fontWeight: "bold",
                  margin: 0,
                  fontFamily: theme.fonts.sans,
                }}
              >
                Insights de IA — Transplante Hepático
              </h3>
              <p
                style={{
                  fontSize: "12px",
                  margin: "2px 0 0",
                  opacity: 0.7,
                  fontFamily: theme.fonts.sans,
                }}
              >
                Portaria 8041/2025 · AASLD Practice Guidance 2024
              </p>
            </div>
          </div>
          {/* AI_16: Badge conta apenas alertas acionáveis */}
          {totalAlertasAcionaveis > 0 && (
            <div
              style={{
                background: alertasAcionaveis.some((a) => a.tipo === "critico")
                  ? "#ef4444"
                  : "#f59e0b",
                borderRadius: theme.borderRadius.full,
                padding: "6px 14px",
                fontSize: "13px",
                fontWeight: "700",
                fontFamily: theme.fonts.sans,
              }}
            >
              {totalAlertasAcionaveis} alerta(s)
            </div>
          )}
        </div>
      </div>

      {/* ─── CONTEÚDO ─── */}
      <div
        style={{
          background: "#f8fafc",
          borderRadius: `0 0 ${theme.borderRadius.xl} ${theme.borderRadius.xl}`,
          border: "1px solid #e2e8f0",
          borderTop: "none",
          padding: "20px",
        }}
      >
        {/* ══════════════════════════════════════════════
            1. ALERTAS ACIONÁVEIS (críticos → alerta → atenção)
               Ordenados por severidade: critico primeiro
            ══════════════════════════════════════════════ */}
        {alertasAcionaveis.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "20px",
            }}
          >
            {alertasAcionaveis
              .sort((a, b) => {
                const ordem = { critico: 0, alerta: 1, atencao: 2, info: 3 };
                return ordem[a.tipo] - ordem[b.tipo];
              })
              .map(renderAlerta)}
          </div>
        )}

        {/* ══════════════════════════════════════════════
            2. LISTA DE ESPERA
            ══════════════════════════════════════════════ */}
        {insights.listaEspera.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #3b82f6",
                borderLeft: "4px solid #3b82f6",
                borderRadius: theme.borderRadius.lg,
                overflow: "hidden",
              }}
            >
              <button
                onClick={() =>
                  setExpandido(
                    expandido === "lista-espera" ? null : "lista-espera"
                  )
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <ClipboardList size={18} color="#1e3a8a" />
                  <div style={{ textAlign: "left" }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#1e3a8a",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Lista de Espera — Fígado
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#3b82f6",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {insights.listaEspera.length} paciente(s) · ordenados por
                      MELD decrescente
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  color="#3b82f6"
                  style={{
                    transform:
                      expandido === "lista-espera"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </button>
              {expandido === "lista-espera" && (
                <div
                  style={{
                    borderTop: "1px solid #bfdbfe",
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {insights.listaEspera.map((pac, idx) => (
                    <div
                      key={pac.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "white",
                        borderRadius: theme.borderRadius.md,
                        border: "1px solid #dbeafe",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                      >
                        <span
                          style={{
                            fontWeight: "700",
                            fontSize: "12px",
                            color: "#94a3b8",
                            width: "20px",
                          }}
                        >
                          {idx + 1}º
                        </span>
                        <div>
                          <p
                            style={{
                              margin: 0,
                              fontWeight: "600",
                              fontSize: "13px",
                              color: "#1e293b",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                window.open(
                                  `${window.location.origin}/prontuario/${pac.id}`,
                                  "_blank"
                                );
                              }}
                              style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                              }}
                              title="Abrir prontuário em nova aba"
                            >
                              {pac.nome}
                            </span>
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              color: "#64748b",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {pac.diasEmLista !== null
                              ? `Em lista há ${formatarTempo(pac.diasEmLista)}`
                              : "Data de inscrição não registrada"}
                            {pac.situacaoEspecial && (
                              <span
                                style={{
                                  marginLeft: "6px",
                                  color: "#f59e0b",
                                  fontWeight: "700",
                                }}
                              >
                                ⭐ SE
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: "700",
                            fontSize: "16px",
                            color:
                              pac.meld >= 25
                                ? "#dc2626"
                                : pac.meld >= 19
                                ? "#f59e0b"
                                : "#3b82f6",
                            fontFamily: theme.fonts.mono,
                          }}
                        >
                          {pac.meld > 0 ? `MELD ${pac.meld}` : "Sem MELD"}
                        </p>
                        {pac.meldData && (
                          <p
                            style={{
                              margin: 0,
                              fontSize: "10px",
                              color: "#94a3b8",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            {formatarData(pac.meldData)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            3. SITUAÇÃO ESPECIAL (painel informativo)
            ══════════════════════════════════════════════ */}
        {alertaSituacaoEspecial && (
          <div style={{ marginBottom: "16px" }}>
            {renderAlerta(alertaSituacaoEspecial)}
          </div>
        )}

        {/* ══════════════════════════════════════════════
            4. TRANSPLANTADOS
            ══════════════════════════════════════════════ */}
        {insights.transplantados.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #22c55e",
                borderLeft: "4px solid #22c55e",
                borderRadius: theme.borderRadius.lg,
                overflow: "hidden",
              }}
            >
              <button
                onClick={() =>
                  setExpandido(
                    expandido === "transplantados" ? null : "transplantados"
                  )
                }
                style={{
                  width: "100%",
                  padding: "14px 16px",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <CheckCircle size={18} color="#14532d" />
                  <div style={{ textAlign: "left" }}>
                    <p
                      style={{
                        margin: 0,
                        fontWeight: "700",
                        fontSize: "14px",
                        color: "#14532d",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      Transplantados
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#22c55e",
                        fontFamily: theme.fonts.sans,
                      }}
                    >
                      {insights.transplantados.length} paciente(s) · mais
                      recentes primeiro
                    </p>
                  </div>
                </div>
                <ChevronDown
                  size={16}
                  color="#22c55e"
                  style={{
                    transform:
                      expandido === "transplantados"
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              </button>
              {expandido === "transplantados" && (
                <div
                  style={{
                    borderTop: "1px solid #bbf7d0",
                    padding: "12px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {insights.transplantados.map((pac) => (
                    <div
                      key={pac.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "8px 12px",
                        background: "white",
                        borderRadius: theme.borderRadius.md,
                        border: "1px solid #dcfce7",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            margin: 0,
                            fontWeight: "600",
                            fontSize: "13px",
                            color: "#1e293b",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              window.open(
                                `${window.location.origin}/prontuario/${pac.id}`,
                                "_blank"
                              );
                            }}
                            style={{
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            title="Abrir prontuário em nova aba"
                          >
                            {pac.nome}
                          </span>
                        </p>
                        <p
                          style={{
                            margin: 0,
                            fontSize: "11px",
                            color: "#64748b",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          Tx: {formatarData(pac.dataTx)} ·{" "}
                          {formatarTempo(pac.diasPosTx)} pós-Tx
                          {pac.tipoAtendimento && ` · ${pac.tipoAtendimento}`}
                        </p>
                        {pac.complicacoes.length > 0 && (
                          <p
                            style={{
                              margin: "2px 0 0",
                              fontSize: "11px",
                              color: "#dc2626",
                              fontWeight: "600",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            ⚠ {pac.complicacoes.join(" · ")}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            padding: "2px 8px",
                            borderRadius: "999px",
                            background:
                              pac.complicacoes.length > 0
                                ? "#fee2e2"
                                : "#dcfce7",
                            color:
                              pac.complicacoes.length > 0
                                ? "#dc2626"
                                : "#15803d",
                            fontWeight: "600",
                            fontFamily: theme.fonts.sans,
                          }}
                        >
                          {pac.complicacoes.length > 0 ? "Atenção" : "Estável"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════════════
            5. PENDÊNCIAS (das consultas)
            ══════════════════════════════════════════════ */}

        {/* ══════════════════════════════════════════════
            6. DISTRIBUIÇÃO MELD
            ══════════════════════════════════════════════ */}
        {insights.distribuicaoMeld.total > 0 && (
          <div
            style={{
              background: "white",
              border: "1px solid #e2e8f0",
              borderRadius: theme.borderRadius.lg,
              padding: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "12px",
              }}
            >
              <BarChart2 size={18} color="#334155" />
              <span
                style={{
                  fontSize: "14px",
                  fontWeight: "700",
                  color: "#334155",
                  fontFamily: theme.fonts.sans,
                }}
              >
                Distribuição MELD — Inscritos em Lista (
                {insights.distribuicaoMeld.total})
              </span>
            </div>

            {/* Barra de distribuição */}
            <div
              style={{
                display: "flex",
                height: "28px",
                borderRadius: theme.borderRadius.md,
                overflow: "hidden",
                border: "1px solid #e2e8f0",
                marginBottom: "10px",
              }}
            >
              {[
                {
                  valor: insights.distribuicaoMeld.meld6a10,
                  cor: "#10b981",
                  label: "6-10",
                },
                {
                  valor: insights.distribuicaoMeld.meld11a18,
                  cor: "#f59e0b",
                  label: "11-18",
                },
                {
                  valor: insights.distribuicaoMeld.meld19a25,
                  cor: "#f97316",
                  label: "19-25",
                },
                {
                  valor: insights.distribuicaoMeld.meldMaior25,
                  cor: "#ef4444",
                  label: ">25",
                },
                {
                  valor: insights.distribuicaoMeld.semMeld,
                  cor: "#94a3b8",
                  label: "N/R",
                },
              ].map((faixa) => {
                const pct =
                  insights.distribuicaoMeld.total > 0
                    ? (faixa.valor / insights.distribuicaoMeld.total) * 100
                    : 0;
                if (pct === 0) return null;
                return (
                  <div
                    key={faixa.label}
                    style={{
                      width: `${pct}%`,
                      background: faixa.cor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "700",
                      fontFamily: theme.fonts.sans,
                      minWidth: pct > 5 ? undefined : "0",
                    }}
                    title={`MELD ${faixa.label}: ${
                      faixa.valor
                    } paciente(s) (${pct.toFixed(0)}%)`}
                  >
                    {pct >= 12 ? `${faixa.label}` : ""}
                  </div>
                );
              })}
            </div>

            {/* Legenda */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "12px",
                justifyContent: "center",
              }}
            >
              {[
                {
                  valor: insights.distribuicaoMeld.meld6a10,
                  cor: "#10b981",
                  label: "MELD 6-10",
                  sub: "Baixo",
                },
                {
                  valor: insights.distribuicaoMeld.meld11a18,
                  cor: "#f59e0b",
                  label: "MELD 11-18",
                  sub: "val. 3m",
                },
                {
                  valor: insights.distribuicaoMeld.meld19a25,
                  cor: "#f97316",
                  label: "MELD 19-25",
                  sub: "val. 1m",
                },
                {
                  valor: insights.distribuicaoMeld.meldMaior25,
                  cor: "#ef4444",
                  label: "MELD >25",
                  sub: "val. 7d",
                },
                {
                  valor: insights.distribuicaoMeld.semMeld,
                  cor: "#94a3b8",
                  label: "Sem MELD",
                  sub: "",
                },
              ].map((faixa) => (
                <div
                  key={faixa.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "11px",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  <div
                    style={{
                      width: "10px",
                      height: "10px",
                      borderRadius: "3px",
                      background: faixa.cor,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ color: "#475569", fontWeight: "600" }}>
                    {faixa.label}:
                  </span>
                  <span style={{ color: "#64748b", fontWeight: "700" }}>
                    {faixa.valor}
                  </span>
                  {faixa.sub && (
                    <span style={{ color: "#94a3b8", fontSize: "10px" }}>
                      ({faixa.sub})
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CALIBRAÇÃO DE SEMÁFOROS ── */}
        {calibStats && calibStats.total > 0 && (
          <div
            style={{
              background: "white",
              borderRadius: theme.borderRadius.lg,
              border: "1px solid #e2e8f0",
              overflow: "hidden",
              marginBottom: "8px",
            }}
          >
            {/* Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
                padding: "10px 16px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: "pointer",
              }}
              onClick={() =>
                setExpandido(expandido === "calibracao" ? null : "calibracao")
              }
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  color: "white",
                }}
              >
                <span style={{ fontSize: "16px" }}>🎯</span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: "700",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  Calibração de Análises IA
                </span>
                <span
                  style={{
                    background: "rgba(255,255,255,0.25)",
                    padding: "2px 10px",
                    borderRadius: theme.borderRadius.full,
                    fontSize: "11px",
                    fontWeight: "700",
                    fontFamily: theme.fonts.sans,
                  }}
                >
                  {calibStats.total} avaliação
                  {calibStats.total > 1 ? "ões" : ""}
                </span>
              </div>
              <span
                style={{
                  color: "white",
                  fontSize: "14px",
                  transform:
                    expandido === "calibracao"
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                  transition: "transform 0.2s",
                }}
              >
                ▼
              </span>
            </div>

            {expandido === "calibracao" && (
              <div style={{ padding: "12px 16px" }}>
                {/* Barras de concordância */}
                {(() => {
                  const pct = (n: number) =>
                    calibStats.total > 0
                      ? Math.round((n / calibStats.total) * 100)
                      : 0;
                  const barras = [
                    {
                      label: "🟢 Concordo",
                      valor: calibStats.concordo,
                      pct: pct(calibStats.concordo),
                      cor: "#16a34a",
                    },
                    {
                      label: "🟡 Parcial",
                      valor: calibStats.parcial,
                      pct: pct(calibStats.parcial),
                      cor: "#ca8a04",
                    },
                    {
                      label: "🔴 Discordo",
                      valor: calibStats.discordo,
                      pct: pct(calibStats.discordo),
                      cor: "#dc2626",
                    },
                  ];
                  return (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        marginBottom: "12px",
                      }}
                    >
                      {barras.map((b) => (
                        <div key={b.label}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "3px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: "600",
                                fontFamily: theme.fonts.sans,
                                color: "#374151",
                              }}
                            >
                              {b.label}
                            </span>
                            <span
                              style={{
                                fontSize: "12px",
                                fontFamily: theme.fonts.sans,
                                color: "#6b7280",
                              }}
                            >
                              {b.valor} ({b.pct}%)
                            </span>
                          </div>
                          <div
                            style={{
                              height: "8px",
                              background: "#f1f5f9",
                              borderRadius: "4px",
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                width: `${b.pct}%`,
                                height: "100%",
                                background: b.cor,
                                borderRadius: "4px",
                                transition: "width 0.5s ease",
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* B227 (AI_114): onde a IA mais erra — por categoria */}
                {calibStats.porCategoria.length > 0 && (
                  <div style={{ marginBottom: "12px" }}>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#64748b",
                        fontFamily: theme.fonts.sans,
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Onde a IA mais erra (categoria)
                    </div>
                    {calibStats.porCategoria.map((c) => (
                      <div
                        key={c.categoria}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "12px",
                          fontFamily: theme.fonts.sans,
                          color: "#374151",
                          padding: "3px 0",
                        }}
                      >
                        <span>{c.label}</span>
                        <span style={{ fontWeight: 700, color: "#b45309" }}>
                          {c.n}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Últimas avaliações com observação */}
                {calibStats.recentes.filter((r) => r.observacao).length > 0 && (
                  <div>
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: "700",
                        color: "#64748b",
                        fontFamily: theme.fonts.sans,
                        marginBottom: "6px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Observações recentes
                    </div>
                    {calibStats.recentes
                      .filter((r) => r.observacao)
                      .slice(0, 3)
                      .map((r, i) => {
                        const emoji =
                          r.voto === "concordo"
                            ? "🟢"
                            : r.voto === "parcial"
                            ? "🟡"
                            : "🔴";
                        const pac = pacientes.find(
                          (p) => p.id === r.pacienteId
                        );
                        return (
                          <div
                            key={i}
                            style={{
                              background: "#f8fafc",
                              border: "1px solid #e2e8f0",
                              borderRadius: theme.borderRadius.md,
                              padding: "8px 10px",
                              marginBottom: "4px",
                              fontSize: "12px",
                              fontFamily: theme.fonts.sans,
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", color: "#374151" }}
                            >
                              {emoji}{" "}
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (pac?.id) {
                                    window.open(
                                      `${window.location.origin}/prontuario/${pac.id}`,
                                      "_blank"
                                    );
                                  }
                                }}
                                style={{
                                  cursor: pac?.id ? "pointer" : "default",
                                  textDecoration: pac?.id
                                    ? "underline"
                                    : "none",
                                }}
                                title={
                                  pac?.id ? "Abrir prontuário em nova aba" : ""
                                }
                              >
                                {pac ? pac.nome.split(" ")[0] : "Paciente"}
                              </span>
                            </span>
                            <span style={{ color: "#64748b" }}>
                              {" "}
                              — {r.observacao}
                            </span>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── SEM ALERTAS ── */}
        {alertasAcionaveis.length === 0 &&
          insights.distribuicaoMeld.total === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                color: "#64748b",
                fontFamily: theme.fonts.sans,
                fontSize: "14px",
              }}
            >
              <span
                style={{
                  fontSize: "36px",
                  display: "block",
                  marginBottom: "8px",
                }}
              >
                ✅
              </span>
              Nenhum alerta no momento. Todos os exames e prazos estão em dia.
            </div>
          )}

        {alertasAcionaveis.length === 0 &&
          insights.distribuicaoMeld.total > 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "12px",
                color: "#16a34a",
                fontFamily: theme.fonts.sans,
                fontSize: "13px",
                background: "#f0fdf4",
                borderRadius: theme.borderRadius.md,
                marginTop: "12px",
                border: "1px solid #bbf7d0",
              }}
            >
              ✅ Nenhum alerta pendente — todos os MELD e exames de imagem estão
              em dia
            </div>
          )}
      </div>
    </div>
  );
}
